<template>
  <v-container fluid class="pa-0 fill-height">
    <v-app-bar app color="primary" dark flat>
      <v-btn icon @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="font-weight-bold">{{
        profile && profile.Creator && profile.Creator.name
      }}</v-toolbar-title>
    </v-app-bar>

    <v-container class="text-center py-6">
      <v-avatar size="90" class="mb-3">
        <v-img
          v-if="profile.gender === 'Female'"
          src="@/assets/avatars/wife.png"
        />
        <v-img v-else src="@/assets/avatars/brother.png" />
      </v-avatar>

      <div class="text-h5 font-weight-bold">{{ profile.name }}</div>
      <div class="text-subtitle-2 grey--text">{{ profile.gender }}</div>

      <!-- ❤️ Likes / 👁️ Views Counters -->
      <div class="d-flex justify-center mt-2">
        <v-chip
          label
          color="pink lighten-5"
          text-color="pink darken-2"
          class="ma-1 font-weight-medium"
        >
          <v-icon left small color="pink darken-2">mdi-heart</v-icon>
          {{ profile.likeCount || 0 }} Likes
        </v-chip>

        <v-chip
          label
          color="blue lighten-5"
          text-color="blue darken-2"
          class="ma-1 font-weight-medium"
        >
          <v-icon left small color="blue darken-2">mdi-eye</v-icon>
          {{ profile.viewCount || 0 }} Views
        </v-chip>
      </div>

      <!-- 🔹 Info Banner -->
      <v-alert
        type="info"
        dense
        color="warning"
        class="mt-4 mx-auto rounded-lg text-left info-banner"
        max-width="500"
      >
        In <strong>NikahConnect</strong>, you’ll be messaging the
        <strong>guardian</strong> of this profile —
        <em>{{ profile?.Creator?.name || "Guardian" }}</em
        >, not <em>{{ profile.name }}</em> directly.
        <br />

        <!-- Guardian online status below -->
        <div class="d-flex align-center mt-2">
          <v-icon left color="primary" small>mdi-account-heart</v-icon>
          <span class="font-weight-medium">
            Guardian:
            <strong>{{
              (profile && profile.Creator && profile.Creator.name) || "Guardian"
            }}</strong>
          </span>

          <!-- 🟢 Guardian online chip -->
          <UserOnlineStatus
            v-if="profile && profile.Creator && profile.Creator.id"
            class="ml-2"
            :userId="profile.Creator.id"
          />
        </div>
      </v-alert>

      <!-- 💚 Compatibility Bar -->
      <v-container v-if="matchResult" class="mt-5">
        <v-card class="rounded-xl pa-4 glass-bar">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="font-weight-bold text--primary"
              >Match Compatibility</span
            >
            <span
              class="font-weight-bold"
              :class="{
                'green--text': matchResult.score > 75,
                'orange--text':
                  matchResult.score <= 75 && matchResult.score >= 50,
                'red--text': matchResult.score < 50,
              }"
            >
              {{ matchResult.score }}%
            </span>
          </div>

          <v-progress-linear
            :value="matchResult.score"
            height="10"
            rounded
            :color="
              matchResult.score > 75
                ? 'green'
                : matchResult.score >= 50
                ? 'orange'
                : 'red'
            "
          ></v-progress-linear>

          <!-- Optional breakdown -->
          <div class="mt-2 text-caption grey--text text--darken-1">
            <v-icon
              color="green"
              small
              v-for="m in matchResult.breakdown.filter((b) => b.matched)"
              :key="m.label"
            >
              mdi-check-circle
            </v-icon>
            <v-icon
              color="orange"
              small
              v-for="m in matchResult.breakdown.filter((b) => !b.matched)"
              :key="m.label"
            >
              mdi-alert-circle-outline
            </v-icon>
            <span class="ml-2"
              >{{ matchResult.breakdown.length }} criteria checked</span
            >
          </div>
        </v-card>
      </v-container>

      <!-- 🔹 Interactive Action Dock -->
      <v-container class="mt-3 text-center">
        <v-row align="center" justify="center" class="text-center">
          <!-- ❤️ Favorite -->
          <v-col cols="auto" class="px-5">
            <div class="d-flex flex-column align-center">
              <v-btn icon large class="favorite-btn" @click="toggleFavorite">
                <v-icon
                  :color="isFavorited ? 'red darken-2' : 'grey darken-1'"
                  class="favorite-icon"
                >
                  {{ isFavorited ? "mdi-heart" : "mdi-heart-outline" }}
                </v-icon>
              </v-btn>
              <span
                class="caption mt-1"
                :class="
                  isFavorited
                    ? 'red--text text--darken-2 font-weight-medium'
                    : 'grey--text'
                "
              >
                {{ isFavorited ? "In Favorites" : "Add to Favorites" }}
              </span>
            </div>
          </v-col>

          <!-- 💬 Message -->
          <v-col cols="auto" class="px-5">
            <div class="d-flex flex-column align-center">
              <v-btn
                icon
                large
                color="primary"
                class="message-btn"
                @click="startChatWithCreator"
              >
                <v-icon>mdi-chat</v-icon>
              </v-btn>
              <span class="caption mt-1 grey--text text--darken-1">
                {{ hasExistingChat ? "Continue Chat" : "Message Guardian" }}
              </span>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </v-container>

    <!-- 🟢 Profile Sections -->
    <v-container class="pb-5">
      <v-card class="mb-6" outlined rounded="xl">
        <v-card-text>
          <v-expansion-panels flat>
            <v-expansion-panel>
              <v-expansion-panel-header>
                Detailed Compatibility
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                <v-list dense class="rounded-xl">
                  <v-list-item
                    v-for="b in matchResult.breakdown"
                    :key="b.label"
                    :class="b.matched ? 'green lighten-5' : 'red lighten-5'"
                  >
                    <v-list-item-icon>
                      <v-icon :color="b.matched ? 'green' : 'red'">
                        {{
                          b.matched ? "mdi-check-circle" : "mdi-close-circle"
                        }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="font-weight-medium">
                        {{ b.label }}
                      </v-list-item-title>
                      <v-list-item-subtitle>
                        {{
                          b.matched
                            ? "Matches your preference"
                            : "Does not match"
                        }}
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>

      <v-card
        v-for="(section, idx) in sections"
        :key="idx"
        class="mb-6"
        outlined
        rounded="xl"
      >
        <v-card-title class="font-weight-bold text--primary">
          <v-icon left color="primary">{{ section.icon }}</v-icon>
          {{ section.title }}
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text>
          <v-row>
            <v-col
              v-for="(item, index) in section.items"
              :key="index"
              cols="12"
              sm="6"
              class="py-1"
            >
              <div class="text-caption grey--text text--darken-1">
                {{ item.label }}
              </div>
              <div class="font-weight-medium text-body-2">
                {{ displayValue(item.key) }}
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-container>
  </v-container>
</template>

<script>
import profileService from "@/modules/Profile/service";
import chatService from "@/modules/Chat/service.js";
import UserOnlineStatus from "@/components/UserOnlineStatus.vue";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      isFavorited: false,
      hasExistingChat: false,
      profile: {},
      completionPercent: 0,
      sections: [
        {
          title: "Bio",
          icon: "mdi-note-outline",
          items: [{ label: "Bio", key: "bio" }],
        },
        {
          title: "Basic Information",
          icon: "mdi-account-circle-outline",
          items: [
            { label: "Full Name", key: "name" },
            { label: "Gender", key: "gender" },
            { label: "Date of Birth", key: "dob" },
            { label: "Country", key: "country" },
            { label: "State", key: "state" },
            { label: "Marital Status", key: "maritalStatus" },
          ],
        },

        {
          title: "Physical Details",
          icon: "mdi-weight-lifter",
          items: [
            { label: "Appearance", key: "appearance" },
            { label: "Height (Feet)", key: "heightRaw" },
            { label: "Weight (lbs)", key: "weightLbs" },
            { label: "Build", key: "build" },
            { label: "Complexion", key: "complexion" },
          ],
        },
        {
          title: "Ethnicity & Origin",
          icon: "mdi-earth",
          items: [
            { label: "Ethnicity", key: "ethnicity" },
            { label: "Inner Caste / Community", key: "innerCaste" },
          ],
        },
        {
          title: "Faith & Religious Practice",
          icon: "mdi-mosque-outline",
          items: [
            { label: "Madhab (School of Thought)", key: "madhab" },
            { label: "Prayer Habit", key: "prayerFrequency" },
            { label: "Prays in Jamaat", key: "praysInJamaat" },
            { label: "Fasts in Ramadan", key: "fastInRamadan" },
            { label: "Gives Zakat", key: "givesZakat" },
            { label: "Masjid Name", key: "masjidName" },
            { label: "Masjid Address", key: "masjidAddress" },
            { label: "Islamic Education Level", key: "islamicEducationLevel" },
            { label: "Hajj Status", key: "hajjStatus" },
            { label: "Umrah Status", key: "umrahStatus" },
          ],
        },
        {
          title: "Lifestyle & Personality",
          icon: "mdi-heart-outline",
          items: [
            { label: "Personality Type", key: "personalityType" },
            { label: "Diet Type", key: "dietType" },
            { label: "Living Preference", key: "livingPreference" },
            { label: "Enjoys Hosting Guests", key: "hostGuests" },
            { label: "Likes to Travel", key: "travelLover" },
            { label: "Home Atmosphere", key: "homeAtmosphere" },
            { label: "Cooking Skill", key: "cookingSkill" },
            { label: "Pre-Marriage Counseling", key: "preMarriageCounseling" },
          ],
        },
        {
          title: "Education & Career",
          icon: "mdi-school-outline",
          items: [
            { label: "Education Level", key: "educationLevel" },
            { label: "Legal Status", key: "legalStatus" },
            { label: "Occupation Type", key: "occupationType" },
            { label: "Profession", key: "profession" },
            { label: "Employment Status", key: "employmentStatus" },
            { label: "Work Environment", key: "workEnvironment" },
            { label: "Travel for Work", key: "travelForWork" },
            { label: "Can Drive", key: "canDrive" },
            { label: "Owns Car", key: "hasCar" },
            { label: "Vehicle Type", key: "vehicleType" },
          ],
        },
        {
          title: "Family & Marriage",
          icon: "mdi-account-group-outline",
          items: [
            { label: "Has Children", key: "hasChildren" },
            { label: "Wants Children", key: "wantsChildren" },
            { label: "Father Alive", key: "fatherAlive" },
            { label: "Father Location", key: "fatherLocation" },
            { label: "Mother Alive", key: "motherAlive" },
            { label: "Mother Location", key: "motherLocation" },
            { label: "Has Siblings", key: "hasSiblings" },
            { label: "Number of Brothers", key: "brotherCount" },
            { label: "Number of Sisters", key: "sisterCount" },
            { label: "Sibling Order", key: "siblingOrder" },
            { label: "Living Arrangement", key: "livingArrangement" },
          ],
        },
        {
          title: "Marriage Expectations",
          icon: "mdi-ring",
          items: [
            { label: "Mehr Considered", key: "mehrConsidered" },
            { label: "Mehr Amount / Form", key: "mehrAmount" },
            { label: "Mehr Notes", key: "mehrNotes" },
            { label: "Wife Work Preference", key: "wifeWorkPreference" },
            {
              label: "Financial Responsibility",
              key: "financialResponsibility",
            },
            { label: "Domestic Contribution", key: "domesticContribution" },
            { label: "Supports Education", key: "supportsEducation" },
            { label: "Post-Marriage Work", key: "postMarriageWork" },
          ],
        },
        {
          title: "Health & Habits",
          icon: "mdi-heart-pulse",
          items: [
            { label: "Has Disability", key: "hasDisability" },
            { label: "Disability Detail", key: "disabilityDetail" },
            { label: "Smokes", key: "smokes" },
            { label: "Drinks", key: "drinks" },
          ],
        },
      ],
    };
  },
  components: { UserOnlineStatus },
  computed: {
    ...mapGetters("global", ["currentUser", "userPreferences"]),
    matchResult() {
      console.log("userPreferences", this.userPreferences);
      console.log("profile", this.profile);
      return this.calculateMatchScore(this.profile, this.userPreferences);
    },
    progressColor() {
      if (this.completionPercent < 30) return "error";
      if (this.completionPercent < 70) return "warning";
      return "success";
    },
    progressMessage() {
      if (this.completionPercent < 25)
        return "😅 Add more info — matches can’t read your mind yet!";
      if (this.completionPercent < 50)
        return "🧐 Getting there! The more you share, the better the match.";
      if (this.completionPercent < 75)
        return "👍 Great effort! You're building a complete story.";
      if (this.completionPercent < 100)
        return "✨ Almost perfect — your future match will appreciate this!";
      return "💯 Excellent! You're 100% match-ready!";
    },
  },
  async created() {
    try {
      const id =
        this.$route.params.profileId ||
        this.profileId ||
        sessionStorage.getItem("lastProfileId");

      if (!id) {
        this.$swal({
          icon: "error",
          title: "Profile Not Found",
          text: "Please select a valid profile.",
          confirmButtonText: "Go Back",
        }).then(() => {
          this.$router.push({ name: "profile-list" });
        });
        return;
      }

      sessionStorage.setItem("lastProfileId", id);

      // ✅ Determine if this page load is a “view”
      const isView = this.$route.query.view === "true";
      // ✅ Pass the query parameter to backend
      const result = await profileService.getById(id, { view: isView });

      this.profile = result.data || result;
      this.isFavorited = this.profile?.isFavorited || false;
      this.calculateCompletion();

      console.log("this.profile", this.profile);
      const creatorId = this.profile?.Creator?.id;
      if (creatorId) {
        // Check if we already have a conversation with this guardian
        const conversations = await chatService.getAll(); // assuming getAll() returns all user conversations
        const match = conversations.data?.rows?.find(
          (c) =>
            (c.UserAId === creatorId || c.UserBId === creatorId) &&
            c.ProfileId === this.profile.id
        );
        this.hasExistingChat = !!match;
      }
    } catch (error) {
      console.error("Error loading profile", error);
      this.$swal({
        icon: "error",
        title: "Unable to Load Profile",
        text: "This profile could not be found or may have been removed.",
        confirmButtonText: "Back to List",
      }).then(() => {
        this.$router.push({ path: "/" });
      });
    }
  },
  methods: {
    normalizeGender(value) {
      if (!value) return "";
      const val = value.toString().trim().toLowerCase();
      if (["female", "femail", "femal"].includes(val)) return "female";
      if (["male", "mail", "mal"].includes(val)) return "male";
      return val;
    },
    calculateMatchScore(profile, prefs) {
      if (!profile || !prefs) return { score: 0, breakdown: [] };

      let total = 0;
      let score = 0;
      const breakdown = [];

      function addResult(label, matched, weight) {
        total += weight;
        if (matched) score += weight;
        breakdown.push({ label, matched, weight });
      }

      const age = this.getAge(profile.dob);

      addResult(
        "Gender",
        this.normalizeGender(prefs.lookingForGender) ===
          this.normalizeGender(profile.gender),
        20
      );
      addResult("Age Range", age >= prefs.ageMin && age <= prefs.ageMax, 15);

      if (prefs.locationType === "specific")
        addResult("Location", prefs.countries?.includes(profile.country), 10);
      else addResult("Location", true, 10);

      if (prefs.madhabType === "specific")
        addResult("Madhab", prefs.madhabs?.includes(profile.madhab), 10);
      else addResult("Madhab", true, 10);

      if (prefs.educationType === "specific")
        addResult(
          "Education",
          prefs.educationLevels?.includes(profile.educationLevel),
          10
        );
      else addResult("Education", true, 10);

      if (prefs.ethnicityType === "specific")
        addResult(
          "Ethnicity",
          prefs.ethnicities?.includes(profile.ethnicity),
          10
        );
      else addResult("Ethnicity", true, 10);

      if (prefs.maritalStatusType === "specific")
        addResult(
          "Marital Status",
          prefs.maritalStatuses?.includes(profile.maritalStatus),
          10
        );
      else addResult("Marital Status", true, 10);

      if (prefs.legalStatusType === "specific")
        addResult(
          "Legal Status",
          prefs.legalStatuses?.includes(profile.legalStatus),
          5
        );
      else addResult("Legal Status", true, 5);

      addResult(
        "Children",
        !profile.hasChildren || prefs.considerWithChildren === "yes",
        5
      );

      addResult(
        "Lifestyle",
        (prefs.smokesPreference === "any" ||
          prefs.smokesPreference === profile.smokes) &&
          (prefs.drinksPreference === "any" ||
            prefs.drinksPreference === profile.drinks),
        5
      );

      return {
        score: Math.round((score / total) * 100),
        breakdown,
      };
    },
    getAge(dob) {
      if (!dob) return 0;
      const d = new Date(dob);
      const diff = Date.now() - d.getTime();
      const age = new Date(diff).getUTCFullYear() - 1970;
      return age;
    },
    async toggleFavorite() {
      try {
        const result = await profileService.toggleLike(this.profile.id);
        console.log("result", result);

        const liked = result.data?.liked; // backend returns true if liked, false if unliked
        this.isFavorited = liked; // ✅ track locally for heart color toggle

        if (liked) {
          this.profile.likeCount++;
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 1500,
            })
            .fire({
              icon: "success",
              title: "Added to favorites ❤️",
            });
        } else {
          this.profile.likeCount = Math.max(0, this.profile.likeCount - 1);
          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 1500,
            })
            .fire({
              icon: "info",
              title: "Removed from favorites 💔",
            });
        }
      } catch (err) {
        console.error("Error toggling like:", err);
      }
    },
    async startChatWithCreator() {
      try {
        if (!this.profile || !this.profile.Creator) {
          this.$swal({
            icon: "error",
            title: "Missing Guardian Info",
            text: "This profile has no guardian assigned.",
          });
          return;
        }

        const creator = this.profile.Creator;
        const creatorName = creator.name || "Guardian";

        // 💬 Notify user
        this.$swal({
          icon: "info",
          title: `Starting chat with ${creatorName}`,
          text: "You’re messaging the guardian, not the individual directly.",
          showConfirmButton: false,
          timer: 1500,
          toast: true,
          position: "top-end",
        });

        // 🔹 Start or fetch existing conversation
        const startChat = await chatService.startChat({
          targetUserId: creator.id,
          profileId: this.profile.id,
        });

        const conversation = startChat?.data || startChat; // API consistency with your other modules

        console.log("conversation", conversation);

        if (!conversation?.id) {
          throw new Error("Unable to start chat");
        }

        if (this.hasExistingChat && conversation?.id) {
          this.$router.push({
            name: "chat-conversation",
            params: { conversationId: conversation.id },
          });
        } else {
          const newChat = await chatService.startChat({
            targetUserId: creator.id,
            profileId: this.profile.id,
          });
          const convo = newChat?.data || newChat;
          this.hasExistingChat = true;

          this.$router.push({
            name: "chat-conversation",
            params: { conversationId: convo.id },
          });
        }
      } catch (error) {
        console.error("Error starting chat:", error);
        this.$swal({
          icon: "error",
          title: "Unable to start chat",
          text: error.message || "Something went wrong.",
        });
      }
    },
    async toggleProfileActive() {
      try {
        const confirm = await this.$swal({
          title: this.profile.isActive
            ? "Pause this profile?"
            : "Reactivate this profile?",
          text: this.profile.isActive
            ? "It will be hidden from others until you reactivate it."
            : "It will be visible again for matchmaking.",
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, continue",
        });

        if (!confirm.isConfirmed) return;

        const result = await profileService.toggleActive(this.profile.id, {
          isActive: !this.profile.isActive,
        });
        this.profile.isActive = !this.profile.isActive;

        this.$swal({
          icon: "success",
          title: this.profile.isActive
            ? "Profile reactivated!"
            : "Profile paused!",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error(error);
        this.$swal({
          icon: "error",
          title: "Unable to update profile status",
        });
      }
    },
    displayValue(key) {
      const val = this.profile[key];

      if (!val || val === "unspecified" || val === "") return "—";

      // ✅ Format yes/no answers
      const yesKeys = [
        "hasChildren",
        "wantsChildren",
        "praysInJamaat",
        "givesZakat",
        "hasSiblings",
        "fatherAlive",
        "motherAlive",
        "travelLover",
        "hostGuests",
        "travelForWork",
        "fastInRamadan",
        "canDrive",
        "hasCar",
        "hasMotorbike",
        "hasDisability",
        "smokes",
        "preMarriageCounseling",
        "mehrConsidered",
        "drinks",
        "supportsEducation",
      ];

      if (yesKeys.includes(key)) {
        if (val.toLowerCase() === "yes") return "✅ Yes";
        if (val.toLowerCase() === "no") return "❌ No";
      }

      // ✅ Format Date of Birth
      if (key === "dob") {
        const d = new Date(val);
        if (isNaN(d)) return val;

        const today = new Date();
        let age = today.getFullYear() - d.getFullYear();
        const m = today.getMonth() - d.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age--;

        const formatted = d.toLocaleDateString("en-US", {
          month: "long",
          day: "numeric",
          year: "numeric",
        });

        return `${formatted} (${age} years old)`;
      }

      return val;
    },
    calculateCompletion() {
      const total = Object.keys(this.profile || {}).length || 1;
      const answered = Object.values(this.profile).filter(
        (v) => v !== null && v !== "" && v !== "unspecified"
      ).length;
      this.completionPercent = Math.round((answered / total) * 100);
    },
    getStatusColor(status) {
      switch ((status || "").toLowerCase()) {
        case "approved":
          return "green darken-1";
        case "rejected":
          return "red darken-2";
        default:
          return "amber darken-2";
      }
    },
    formatStatus(status) {
      if (!status) return "Pending";
      return status.charAt(0).toUpperCase() + status.slice(1);
    },
    editProfile() {
      this.$router.push({
        name: "profile-edit",
        params: { profileId: this.profile.id },
      });
    },
  },
};
</script>

<style scoped>
.v-avatar {
  border: 3px solid #2a6465;
}
.v-card {
  background-color: #ffffff;
}
.v-card-title {
  font-size: 16px;
}
.v-card-text {
  font-size: 14px;
}
.v-progress-linear {
  transition: 0.4s ease-in-out;
}
@media (max-width: 600px) {
  .v-card-title {
    font-size: 15px;
  }
  .v-card-text {
    font-size: 13px;
  }
}
.favorite-icon,
.message-btn {
  transition: all 0.2s ease;
}

.favorite-btn:hover .favorite-icon {
  transform: scale(1.2);
}

.message-btn:hover {
  transform: scale(1.15);
  background-color: #e3f2fd !important;
}

.caption {
  font-size: 13px !important;
}

.v-icon {
  font-size: 30px !important;
}

@media (max-width: 600px) {
  .v-icon {
    font-size: 26px !important;
  }
  .caption {
    font-size: 12px !important;
  }
}
.glass-bar {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.25);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}
</style>